import React, { useEffect, useState, useRef } from 'react';
import Spinner from '../../Spinner';
import * as turf from "@turf/turf";
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { drawStyle } from '../../drawStyles.js';
import FlatList from 'flatlist-react/lib';
import DispatchArea from './DispatchArea.js';
import left from '../../../assets/icons/left.png';
import axios from 'axios';

export default function Dispatch({ dispatchPerimeter, setDispatchPerimeter, map, myAreas, teamAreas, fire, getToken }) {
    const [stage, setStage] = useState('draw');
    const [dispatchAreas, setDispatchAreas] = useState(null);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedEngine, setSelectedEngine] = useState(null);
    const [isDispatching, setIsDispatching] = useState(false);
    const [dispatchResults, setDispatchResults] = useState(null);

    const draw = useRef();

    const totalAreas = [...teamAreas, ...myAreas];

    const lower = search.toLowerCase().trim();

    // API call function to dispatch to Fulcrum
    const dispatchToFulcrum = async (addresses, engineType) => {
        const payload = {
            fire: {
                fire_id: fire.fire_id || fire.id,
                name: fire.name,
                latitude: fire.latitude,
                longitude: fire.longitude,
                data_source: fire.data_source || "FireWatch",
                active: fire.active !== false,
                containment: fire.perContained || 0,
                area_acres: fire.area_acres || 0
            },
            addresses: addresses.map(address => ({
                id: address.id,
                name: address.name,
                description: address.description,
                type: address.type,
                user_id: address.user_id,
                team_id: address.team_id,
                value: address.value,
                longitude: address.longitude,
                latitude: address.latitude,
                radius_miles: address.radius_miles,
                geometry: address.geometry,
                created_timestamp: address.created_timestamp
            })),
            engine: engineType
        };

        console.log('payload', payload)

        try {
            // First get the token
            const token = await getToken();
            
            const response = await axios.post('https://firewatch-api.space-eyes.com/fulcrum-dispatch', payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                timeout: 30000
            });
            return response.data;
        } catch (error) {
            console.error('Dispatch API error:', error);
            throw error;
        }
    };
    
    const handleDispatch = async () => {
        if (!selectedEngine || selectedAreas.length === 0) {
            alert('Please select an engine type and at least one address');
            return;
        }

        setIsDispatching(true);
        setDispatchResults(null);

        const engineType = `Engine ${selectedEngine}`;

        try {
            const result = await dispatchToFulcrum(selectedAreas, engineType);
            
            setDispatchResults(result.results);

            setStage('draw');
            alert('Dispatch successful. Data has been sent to Fulcrum.');
            
        } catch (error) {
            console.error('Dispatch process error:', error);
            alert('Dispatch failed: ' + error.message);
        } finally {
            setIsDispatching(false);
        }
    };

    const filteredAreas = dispatchAreas?.filter(area => {
    if (lower === '') return true;

    const inName = area.name.toLowerCase().includes(lower);
    const inDesc = area.description
        ? area.description.toLowerCase().includes(lower)
        : false;

    return inName || inDesc;
    });

    useEffect(() => {
        return () => {
            setDispatchPerimeter(null);
            draw.current?.deleteAll()
        }
    }, []);

    useEffect(() => {
        if (stage === 'draw' && selectedAreas.length > 0) {
            setSelectedAreas([])
        }
    }, [stage]);

    useEffect(() => {
        if (!draw.current) {
          draw.current = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true },
            styles: drawStyle,
          });
        }
    
        if (map.current && !map.current._controls.includes(draw.current)) {
          map.current.addControl(draw.current);
        }
    
        if (dispatchPerimeter) {
          draw.current.deleteAll();
          draw.current.add(dispatchPerimeter);
          draw.current.changeMode('simple_select');
        } else {
          draw.current.changeMode('draw_polygon');
        }
    
        const handleUpdate = () => {
          const data = draw.current.getAll();
          if (!data.features.length) return;
    
          const area     = turf.area(data);
          const rounded  = Math.round(area * 100) / 100;
          data.features[0].properties = { area: rounded };
          setDispatchPerimeter(data);
    
          const pts = turf.featureCollection(
            totalAreas.map(a =>
              turf.point([ a.longitude, a.latitude ], a)
            )
          );
    
          const inside = turf.pointsWithinPolygon(pts, data.features[0]);
    
          const enclosedAreas = inside.features.map(f => f.properties);
    
          setDispatchAreas(enclosedAreas);
        };
    
        map.current.on('draw.create', handleUpdate);
        map.current.on('draw.update', handleUpdate);
    
        return () => {
          map.current.off('draw.create', handleUpdate);
          map.current.off('draw.update', handleUpdate);
          if (map.current._controls.includes(draw.current)) {
            map.current.removeControl(draw.current);
          }
        };
    }, [map, dispatchPerimeter, totalAreas, setDispatchPerimeter, setDispatchAreas]);

    const stageSwitch = (stage) => {
        switch (stage) {
            case 'draw':
                return (
                    <div className='fire-popup-dispatch'>
                        <div className='fire-popup-dispatch-container'>
                            <div className='add-fire-content-instructions-instruction'>
                                <div className='add-fire-content-instructions-instruction-title' style={{ color: 'black' }}>Draw Polygon</div>
                                <div 
                                    className='add-fire-content-instructions-instruction-sub-title' 
                                    style={{ 
                                        color: 'black',
                                        marginTop: '0.3rem',
                                        padding: '0.5rem',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px',
                                        borderLeft: '3px solid #dee2e6',
                                        lineHeight: '1.4',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    To select desired PIF's, define a perimeter by plotting and connecting dots on the map. 
                                </div>
                                {dispatchPerimeter && (
                                    <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.7rem' }}>
                                        <div
                                            style={{
                                                width: '49%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.6rem',
                                                fontSize: '0.8rem',
                                                color: 'white',
                                                fontWeight: '600',
                                                borderRadius: '8px',
                                                padding: '0.4rem 0',
                                                cursor: 'pointer',
                                                background: 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => {
                                                setDispatchPerimeter(null);
                                                draw.current.deleteAll();
                                                if (dispatchAreas) {
                                                    setDispatchAreas(null);
                                                }
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = 'linear-gradient(135deg, #5f5f5f 0%, #404040 100%)';
                                                e.target.style.transform = 'translateY(-1px)';
                                                e.target.style.boxShadow = '0 4px 8px rgba(140, 140, 140, 0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)';
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        >
                                            Discard
                                        </div>
                                        <div
                                            style={{
                                                width: '49%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.6rem',
                                                fontSize: '0.8rem',
                                                color: 'white',
                                                fontWeight: '600',
                                                borderRadius: '8px',
                                                padding: '0.4rem 0',
                                                cursor: dispatchPerimeter ? 'pointer' : 'not-allowed',
                                                background: dispatchPerimeter 
                                                    ? 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)'
                                                    : 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => {
                                                if (dispatchPerimeter) {
                                                    setStage('areas');
                                                }
                                            }}
                                            onMouseEnter={(e) => {
                                                if (dispatchPerimeter) {
                                                    e.target.style.background = 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
                                                    e.target.style.transform = 'translateY(-1px)';
                                                    e.target.style.boxShadow = '0 4px 8px rgba(0, 123, 255, 0.3)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (dispatchPerimeter) {
                                                    e.target.style.background = 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)';
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = 'none';
                                                }
                                            }}
                                        >
                                            Next
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            case 'areas':
                return (
                    <div className='fire-popup-dispatch'>
                         <div className='fire-popup-back-btn' 
                            onClick={() => {
                                setStage('draw');
                            }}
                        >
                            <img 
                                src={left} 
                                className="fire-popup-back-btn-icon"
                            />
                            Back to draw
                        </div>
                        <div className="all-fires-search" style={{ marginTop: '-0.4rem' }}>
                            <input
                                type="text"
                                className="all-fires-search-input"
                                placeholder="Search by address/name"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        {(dispatchAreas && dispatchAreas.length > 0) && <div style={{ fontSize: '0.9rem', color: 'black', fontWeight: 'bold', marginTop: '0.7rem' }}>({dispatchAreas.length}) PIF's found inside dispatch polygon</div>}
                        <div className='fire-popup-dispatch-container' style={{ marginTop: '0.5rem' }}>
                            {!dispatchAreas ? 
                                <div style={{ paddingTop: '1rem' }}>
                                    <Spinner />
                                </div>
                                : 
                                dispatchAreas?.length === 0 ?
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    padding: '2rem',
                                    textAlign: 'center',
                                    color: '#666'
                                }}>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                        No PIFs found in this polygon
                                    </div>
                                    <div style={{ fontSize: '0.9rem' }}>
                                        Try drawing a different polygon or check your area coverage
                                    </div>
                                </div>
                                :
                                <div style={{ overflowY: 'auto', maxHeight: 200, paddingBottom: '1rem' }}>
                                    <FlatList
                                        list={filteredAreas}
                                        renderItem={(area, idx) => (
                                            <div style={{ marginBottom: idx === filteredAreas.length - 1 ? '1rem' : '0' }}>
                                                <DispatchArea 
                                                    area={area}
                                                    selectedAreas={selectedAreas}
                                                    setSelectedAreas={setSelectedAreas}
                                                />
                                            </div>
                                        )}
                                        renderOnScroll
                                        renderWhenEmpty={() => (
                                            <div style={{ 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                padding: '2rem',
                                                textAlign: 'center',
                                                color: '#666'
                                            }}>
                                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                                    No PIFs found in this polygon
                                                </div>
                                                <div style={{ fontSize: '0.9rem' }}>
                                                    Try drawing a different polygon or check your area coverage
                                                </div>
                                            </div>
                                        )}
                                    />
                                </div>
                                }
                            {(dispatchAreas && dispatchAreas.length > 0) && <div className='create-area-content-form-btns'>
                                {selectedAreas.length === dispatchAreas?.length ?
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.6rem',
                                        fontSize: '0.8rem',
                                        color: 'white',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        padding: '0.4rem 0',
                                        cursor: 'pointer',
                                        background: 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                        setSelectedAreas([]);
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #5f5f5f 0%, #404040 100%)';
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(140, 140, 140, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)';
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                >
                                    Deselect All
                                </div>
                                :
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.6rem',
                                        fontSize: '0.8rem',
                                        color: 'white',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        padding: '0.4rem 0',
                                        cursor: 'pointer',
                                        background: 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                        setSelectedAreas(dispatchAreas);
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #5f5f5f 0%, #404040 100%)';
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(140, 140, 140, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)';
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                >
                                    Select All
                                </div>}
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.6rem',
                                        fontSize: '0.8rem',
                                        color: 'white',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        padding: '0.4rem 0',
                                        cursor: selectedAreas.length > 0 ? 'pointer' : 'not-allowed',
                                        background: selectedAreas.length > 0 
                                            ? 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)'
                                            : 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                        if (selectedAreas.length > 0) {
                                            setStage('engines')
                                        }
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedAreas.length > 0) {
                                            e.target.style.background = 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 8px rgba(0, 123, 255, 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedAreas.length > 0) {
                                            e.target.style.background = 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    Next ({selectedAreas.length})
                                </div>
                            </div>}
                        </div>
                    </div>
                )
            case 'engines':
                return (
                    <div className='fire-popup-dispatch'>
                            <div className='fire-popup-back-btn' 
                            onClick={() => {
                                setStage('areas');
                            }}
                        >
                            <img 
                                src={left} 
                                className="fire-popup-back-btn-icon"
                            />
                            Back to PIF's
                        </div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'black', paddingTop: '0.5rem' }}>Choose Engine</div>
                        <div className='fire-popup-dispatch-container' style={{ marginTop: '0.5rem' }}>
                                <div style={{ maxHeight: 200, overflowY: 'auto', paddingBottom: '1rem' }}>
                                    <FlatList
                                        list={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
                                        renderItem={(engineType, idx) => (
                                            <div 
                                                style={{ 
                                                    width: '100%', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: '0.6rem', 
                                                    justifyContent: 'flex-start', 
                                                    background: selectedEngine === engineType ? '#e3f2fd' : 'white', 
                                                    padding: '0.7rem', 
                                                    borderRadius: 10, 
                                                    marginBottom: idx === 8 ? '1.5rem' : '0.5rem',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setSelectedEngine(engineType)}
                                            >
                                                <input 
                                                    type='radio' 
                                                    checked={selectedEngine === engineType}
                                                    onChange={() => setSelectedEngine(engineType)}
                                                />
                                                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'black' }}>Engine {engineType}</div>
                                            </div>
                                        )}
                                        renderOnScroll
                                        renderWhenEmpty={() => (
                                            <div
                                                style={{
                                                    fontSize: '0.9rem',
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    paddingTop: '0.5rem',
                                                }}
                                            >
                                            There are no PIF's in this polygon.
                                            </div>
                                        )}
                                    />
                            </div>
                            {isDispatching ?
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                    <Spinner />
                                </div>
                                :
                                <div className='create-area-content-form-btns'>
                                <div
                                    style={{ 
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.6rem',
                                        fontSize: '0.8rem',
                                        color: 'white',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        padding: '0.4rem 0',
                                        cursor: (isDispatching || !selectedEngine || selectedAreas.length === 0) ? 'not-allowed' : 'pointer',
                                        background: (isDispatching || !selectedEngine || selectedAreas.length === 0)
                                            ? 'linear-gradient(135deg, #8c8c8c 0%, #5f5f5f 100%)'
                                            : 'linear-gradient(135deg, #ff6846 0%, #e15e41 100%)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={(isDispatching || !selectedEngine || selectedAreas.length === 0) ? undefined : handleDispatch}
                                    onMouseEnter={(e) => {
                                        if (!isDispatching && selectedEngine && selectedAreas.length > 0) {
                                            e.target.style.background = 'linear-gradient(135deg, #e15e41 0%, #c54e35 100%)';
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 8px rgba(255, 104, 70, 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isDispatching && selectedEngine && selectedAreas.length > 0) {
                                            e.target.style.background = 'linear-gradient(135deg, #ff6846 0%, #e15e41 100%)';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    {`Dispatch (${selectedAreas.length})`}
                                </div>
                            </div>}
                        </div>
                    </div>
                )
                case 'personnel':
                    return (
                        <div className='fire-popup-dispatch'>
                                <div className='fire-popup-back-btn' 
                                onClick={() => {
                                    setStage('engines');
                                }}
                            >
                                <img 
                                    src={left} 
                                    className="fire-popup-back-btn-icon"
                                />
                                Back to engines
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'black', paddingTop: '0.5rem' }}>Choose Personnel</div>
                            <div className='fire-popup-dispatch-container' style={{ marginTop: '0.5rem' }}>
                                    <div style={{ maxHeight: 200 }}>
                                        <FlatList
                                            list={['Chris Matthews', 'Daniel Lopez', 'Zachary Thomas']}
                                            renderItem={(name) => (
                                                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', justifyContent: 'flex-start', background: 'white', padding: '0.7rem', borderRadius: 10, marginBottom: '0.5rem' }}>
                                                    <input type='checkbox'/>
                                                    <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'black' }}>{name}</div>
                                                </div>
                                            )}
                                            renderOnScroll
                                            renderWhenEmpty={() => (
                                                <div
                                                    style={{
                                                        fontSize: '0.9rem',
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        paddingTop: '0.5rem',
                                                    }}
                                                >
                                                There are no PIF's in this polygon.
                                                </div>
                                            )}
                                        />
                                </div>
                                <div className='create-area-content-form-btns'>
                                    <div
                                        className='create-area-content-form-btns-btn-cancel'
                                        style={{ width: '100%' }}
                                        onClick={() => {
            
                                        }}
                                    >
                                        Dispatch
                                    </div>
                                </div>
                            </div>
                        </div>
                )
        }
    }

    return (
       stageSwitch(stage)
    )
}